import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play, Shield, ShieldAlert, Cpu, BookOpen, ChevronDown, ChevronRight, Zap, Eye, FolderKanban, Ban } from 'lucide-react'
import { api } from '../api/client'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import type { Tag, Project, PlaygroundAgent } from '../api/types'

type PlaygroundSkill = { id: string; name: string; description: string }

function getDisplayedSkillAssignments(agents: PlaygroundAgent[], skills: PlaygroundSkill[]): PlaygroundSkill[] {
  return agents.flatMap(agent => (
    agent.available ? skills.filter(skill => agent.requires_skills.includes(skill.id)) : []
  ))
}

/** Count the skill rows rendered by the Agents → Skills dependency tree. */
export function countDisplayedSkills(agents: PlaygroundAgent[], skills: PlaygroundSkill[]): number {
  return getDisplayedSkillAssignments(agents, skills).length
}

/** Return API skills represented by at least one available-agent assignment. */
export function getDisplayedSkills(agents: PlaygroundAgent[], skills: PlaygroundSkill[]): PlaygroundSkill[] {
  const displayedSkillIds = new Set(getDisplayedSkillAssignments(agents, skills).map(skill => skill.id))
  return skills.filter(skill => displayedSkillIds.has(skill.id))
}

// ─── Playground Panel ─────────────────────────────────────────────────────────
// Pick a registered project OR manual tags → see the full delivery tree.
// Rendered as the "Playground" tab inside the Catalog page (no page chrome of
// its own — the Catalog page supplies the heading, width, and padding).

export function PlaygroundPanel() {
  const [mode, setMode] = useState<'project' | 'manual'>('project')
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [triggered, setTriggered] = useState(false)

  const toggleTag = (tagId: string) => {
    setTriggered(false)
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(x => x !== tagId) : [...prev, tagId]
    )
  }

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
    staleTime: 60_000,
  })

  const { data: tagData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.registry.getTags(),
    staleTime: 60_000,
  })

  const projects: Project[] = projectsData?.projects ?? []
  const tags: Tag[] = tagData?.tags ?? []
  const categories = ['language', 'framework', 'infra', 'runtime'] as const

  const selectedProject = projects.find(p => (p.project_id || p.id) === selectedProjectId)
  const activeTags = mode === 'project' ? (selectedProject?.profiles ?? []) : selectedTags

  // Only pass a system_id in "From Project" mode — manual-tag mode has no project
  // to check agent restrictions against, so the backend returns every agent as available.
  const activeSystemId = mode === 'project' ? (selectedProject?.project_id || selectedProject?.id) : undefined

  const { data: result, isLoading } = useQuery({
    queryKey: ['playground', activeTags.join(','), activeSystemId ?? ''],
    queryFn: () => api.registry.getPlaygroundPreview(activeTags, activeSystemId),
    enabled: triggered && activeTags.length > 0,
    staleTime: 30_000,
  })

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 'var(--space-3)' }}>
        Select a registered project or pick tags manually to preview exactly what gets delivered to the IDE.
      </p>

      {/* Mode switcher */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 'var(--space-3)', background: 'var(--surface)', borderRadius: 'var(--radius-s)', padding: 3, width: 'fit-content', border: '1px solid var(--border)' }}>
        <button
          onClick={() => { setMode('project'); setTriggered(false) }}
          style={{
            padding: '6px 16px', fontSize: 13, borderRadius: 'var(--radius-s)', cursor: 'pointer',
            border: 'none', fontWeight: mode === 'project' ? 600 : 400,
            background: mode === 'project' ? 'var(--brand)' : 'transparent',
            color: mode === 'project' ? '#fff' : 'var(--text-secondary)',
          }}
        >
          <FolderKanban size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          From Project
        </button>
        <button
          onClick={() => { setMode('manual'); setTriggered(false) }}
          style={{
            padding: '6px 16px', fontSize: 13, borderRadius: 'var(--radius-s)', cursor: 'pointer',
            border: 'none', fontWeight: mode === 'manual' ? 600 : 400,
            background: mode === 'manual' ? 'var(--brand)' : 'transparent',
            color: mode === 'manual' ? '#fff' : 'var(--text-secondary)',
          }}
        >
          <Play size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Manual Tags
        </button>
      </div>

      {/* Project selector */}
      {mode === 'project' && (
        <Card style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
            <FolderKanban size={16} color="var(--brand)" />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Select a registered project</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
            See the exact rules, agents, and skills this project receives based on its configured tags.
          </p>
          {projects.length === 0 ? (
            <div style={{ padding: 'var(--space-2)', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              No projects registered yet. Go to My Projects to onboard one.
            </div>
          ) : (
            <>
              <select
                value={selectedProjectId}
                onChange={e => { setSelectedProjectId(e.target.value); setTriggered(false) }}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-s)', color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)', cursor: 'pointer',
                }}
              >
                <option value="">— Choose a project —</option>
                {projects.map(p => (
                  <option key={p.project_id || p.id} value={p.project_id || p.id}>
                    {p.name} ({(p.profiles || []).join(', ') || 'no tags'})
                  </option>
                ))}
              </select>
              {selectedProject && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
                  {(selectedProject.profiles || []).map(t => (
                    <span key={t} style={{
                      padding: '3px 10px', fontSize: 12, borderRadius: 12,
                      background: 'var(--brand-subtle)', color: 'var(--brand)', fontWeight: 500,
                    }}>{t}</span>
                  ))}
                  {selectedProject.selected_agents && selectedProject.selected_agents.length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 8 }}>
                      + {selectedProject.selected_agents.length} agents selected
                    </span>
                  )}
                </div>
              )}
            </>
          )}
          <Button
            onClick={() => setTriggered(true)}
            disabled={!selectedProjectId || isLoading}
            style={{ width: '100%' }}
          >
            <Eye size={14} style={{ marginRight: 6 }} />
            {isLoading ? 'Resolving...' : 'Preview IDE Delivery'}
          </Button>
        </Card>
      )}

      {/* Manual tag selector */}
      {mode === 'manual' && (
        <Card style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
            <Play size={16} color="var(--brand)" />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Configure tags</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
            Pick technologies manually. Rules are filtered by tags — agents and skills are universal.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
            {categories.map(cat => {
              const catTags = tags.filter(t => t.category === cat)
              if (catTags.length === 0) return null
              return catTags.map(tag => {
                const isSelected = selectedTags.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    style={{
                      padding: '6px 14px', fontSize: 13, borderRadius: 20, cursor: 'pointer',
                      border: isSelected ? '2px solid var(--brand)' : '1px solid var(--border)',
                      background: isSelected ? 'var(--brand-subtle)' : 'var(--surface)',
                      color: isSelected ? 'var(--brand)' : 'var(--text-secondary)',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    {tag.name}
                  </button>
                )
              })
            })}
          </div>
          <Button
            onClick={() => setTriggered(true)}
            disabled={selectedTags.length === 0 || isLoading}
            style={{ width: '100%' }}
          >
            <Eye size={14} style={{ marginRight: 6 }} />
            {isLoading ? 'Resolving...' : 'Preview IDE Delivery'}
          </Button>
        </Card>
      )}

      {/* Results */}
      {triggered && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* Summary strip */}
          <div style={{
            display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface)', borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
            flexWrap: 'wrap', alignItems: 'center',
          }}>
            {mode === 'project' && selectedProject && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 12, borderRight: '1px solid var(--border)', marginRight: 4 }}>
                <FolderKanban size={14} color="var(--brand)" />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedProject.name}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Shield size={14} color="var(--warning)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{result.rules_count} rules</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Cpu size={14} color="var(--brand)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {result.agents_count} agent{result.agents_count === 1 ? '' : 's'}
                {result.agents_total_count > result.agents_count && (
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> of {result.agents_total_count}</span>
                )}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <BookOpen size={14} color="var(--success)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{countDisplayedSkills(result.agents, result.skills)} skills</span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={14} color="var(--text-muted)" />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Tags: {activeTags.join(', ')}
              </span>
            </div>
          </div>

          {/* Agents with dependency tree */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
              <Cpu size={16} color="var(--brand)" />
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Agents → Skills (Dependency Tree)</h3>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              Each agent has skills it depends on. When an agent is active, its skills are auto-loaded.
            </p>

            {/* Access control notice — only shown in "From Project" mode */}
            {result.access_control && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 'var(--space-2)',
                padding: '8px 12px', borderRadius: 'var(--radius-s)',
                background: result.agents_total_count > result.agents_count ? 'rgba(220,50,50,0.06)' : 'var(--surface-hover)',
                border: result.agents_total_count > result.agents_count ? '1px solid rgba(220,50,50,0.25)' : '1px solid var(--border)',
              }}>
                {result.agents_total_count > result.agents_count
                  ? <ShieldAlert size={14} color="var(--error)" style={{ marginTop: 1, flexShrink: 0 }} />
                  : <Shield size={14} color="var(--success)" style={{ marginTop: 1, flexShrink: 0 }} />}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {result.agents_total_count > result.agents_count ? (
                    <>
                      <strong>{result.agents_total_count - result.agents_count} agent{result.agents_total_count - result.agents_count === 1 ? '' : 's'} blocked</strong> for this project —
                      {result.access_control.declared_agents !== null && (
                        <> only <code>{result.access_control.declared_agents.join(', ')}</code> {result.access_control.declared_agents.length === 1 ? 'is' : 'are'} declared in <code>system.yaml</code>.</>
                      )}
                      {result.access_control.allowed_agents !== null && (
                        <> <code>allowed_agents.yaml</code> restricts this repo to <code>{result.access_control.allowed_agents.join(', ')}</code>.</>
                      )}
                    </>
                  ) : (
                    <>No agent restrictions apply — every catalog agent {result.access_control.declared_agents === null ? '(no system.yaml agents list)' : 'is declared'} would be delivered to this project.</>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {result.agents.map(agent => (
                <AgentTreeNode key={agent.id} agent={agent} skills={result.skills} />
              ))}
            </div>
          </Card>

          {/* Rules matched */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
              <Shield size={16} color="var(--warning)" />
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Rules Delivered ({result.rules_count})</h3>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              These rules match the selected tags. Universal rules (no tag filter) are always included.
            </p>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {result.rules.map(rule => (
                <div key={rule.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderBottom: '1px solid var(--border)',
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{rule.id}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 8 }}>{rule.title}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: rule.domain === 'security' ? 'rgba(220,50,50,0.1)' : 'var(--surface-hover)',
                      color: rule.domain === 'security' ? 'var(--error)' : 'var(--text-muted)',
                    }}>{rule.domain}</span>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: rule.severity === 'mandatory' ? 'rgba(220,50,50,0.1)' : 'var(--surface-hover)',
                      color: rule.severity === 'mandatory' ? 'var(--error)' : 'var(--text-muted)',
                    }}>{rule.severity}</span>
                  </div>
                </div>
              ))}
              {result.rules.length === 0 && (
                <div style={{ padding: 'var(--space-3)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No rules matched these tags. Universal rules will still apply.
                </div>
              )}
            </div>
          </Card>

          {/* IDE Preview */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-2)' }}>
              <Zap size={16} color="var(--success)" />
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>IDE Experience Preview</h3>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              This is what developers see when <code>get_startup_context()</code> runs in their IDE:
            </p>
            <pre style={{
              background: 'var(--surface-hover)', padding: 'var(--space-3)',
              borderRadius: 'var(--radius-s)', fontSize: 12, lineHeight: 1.6,
              border: '1px solid var(--border)', overflow: 'auto',
              fontFamily: 'JetBrains Mono, Fira Code, monospace',
            }}>
              {`✓ Context Lake active | tags: ${activeTags.join(', ')}
  Rules: ${result.rules_count} loaded (${result.rules.filter(r => r.severity === 'mandatory').length} mandatory)
  Agents: ${result.agents.filter(a => a.available).map(a => '@' + a.id).join(' · ') || '(none available)'}
  Skills: ${getDisplayedSkills(result.agents, result.skills).map(s => s.id).join(', ')}

  Try:
    "implement JIRA-123"        → @developer with spec-driven workflow
    "review my changes"         → @mr-reviewer with code-review skill
    "write tests for auth.py"   → @test-engineer with TDD skill
    "explain this codebase"     → @onboarding with architecture docs`}
            </pre>
          </Card>
        </div>
      )}
    </div>
  )
}

// ─── Agent Tree Node (expandable) ─────────────────────────────────────────────

function AgentTreeNode({ agent, skills }: Readonly<{
  agent: PlaygroundAgent
  skills: Array<{ id: string; name: string; description: string }>
}>) {
  const [expanded, setExpanded] = useState(true)
  const agentSkills = skills.filter(s => agent.requires_skills.includes(s.id))
  const blocked = agent.available === false

  return (
    <div style={{
      border: blocked ? '1px solid rgba(220,50,50,0.25)' : '1px solid var(--border)',
      borderRadius: 'var(--radius-s)', overflow: 'hidden',
      opacity: blocked ? 0.7 : 1,
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          cursor: 'pointer', background: 'var(--surface)',
          border: 'none', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit',
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Cpu size={14} color={blocked ? 'var(--text-muted)' : 'var(--brand)'} />
        <span style={{ fontWeight: 600, fontSize: 13, textDecoration: blocked ? 'line-through' : 'none' }}>@{agent.id}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>— {agent.description}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {blocked ? (
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 4,
              background: 'rgba(220,50,50,0.1)', color: 'var(--error)',
            }}>
              <Ban size={10} /> Blocked
            </span>
          ) : agentSkills.length > 0 && (
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 10,
              background: 'rgba(34,197,94,0.1)', color: 'var(--success)',
            }}>
              {agentSkills.length} skill{agentSkills.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </button>
      {expanded && blocked && (
        <div style={{
          padding: '8px 12px 8px 40px', fontSize: 12, color: 'var(--error)',
          borderTop: '1px solid var(--border)', background: 'rgba(220,50,50,0.04)',
        }}>
          Not delivered to this project — {agent.blocked_reason}.
        </div>
      )}
      {expanded && !blocked && agentSkills.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {agentSkills.map(skill => (
            <div key={skill.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px 8px 40px', borderBottom: '1px solid var(--border)',
            }}>
              <BookOpen size={12} color="var(--success)" />
              <span style={{ fontSize: 12, fontWeight: 500 }}>{skill.id}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>— {skill.name}</span>
            </div>
          ))}
        </div>
      )}
      {expanded && !blocked && agentSkills.length === 0 && (
        <div style={{
          padding: '8px 12px 8px 40px', fontSize: 12, color: 'var(--text-muted)',
          borderTop: '1px solid var(--border)',
        }}>
          No explicit skill dependencies — uses rules directly.
        </div>
      )}
    </div>
  )
}
